import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ACTIVE_PILLAR_NUMBERS } from '../data/pillars';
import { 
  ChevronLeft, ChevronRight, CheckCircle, Upload, FileText, 
  AlertCircle, HelpCircle, Lightbulb, Star, Sparkles, 
  DollarSign, BarChart3, Award, Clock, ArrowRight, Save, Send
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// C-N-E-S-S Scoring Journey Configuration
const SCORING_JOURNEY = [
  { level: 1, letter: 'C', label: 'Starting', gamified: 'Starting the Journey', icon: '🌱', color: '#94A3B8', bgColor: 'bg-slate-100', borderColor: 'border-slate-300', textColor: 'text-slate-600' },
  { level: 2, letter: 'N', label: 'Building', gamified: 'Building Foundation', icon: '🌿', color: '#60A5FA', bgColor: 'bg-blue-50', borderColor: 'border-blue-300', textColor: 'text-blue-600' },
  { level: 3, letter: 'E', label: 'Growing', gamified: 'Growing Strong', icon: '🌳', color: '#34D399', bgColor: 'bg-emerald-50', borderColor: 'border-emerald-300', textColor: 'text-emerald-600' },
  { level: 4, letter: 'S', label: 'Leading', gamified: 'Leading the Way', icon: '🌲', color: '#A855F7', bgColor: 'bg-purple-50', borderColor: 'border-purple-300', textColor: 'text-purple-600' },
  { level: 5, letter: 'S', label: 'Exemplary', gamified: 'Setting the Standard', icon: '🏆', color: '#F59E0B', bgColor: 'bg-amber-50', borderColor: 'border-amber-300', textColor: 'text-amber-600' },
];

// Assessment Flow Phases
const ASSESSMENT_PHASES = [
  { id: 1, name: 'Qualitative Assessment', shortName: 'Qualitative', icon: FileText, description: 'Self-Assessment', color: 'indigo' },
  { id: 2, name: 'Payment', shortName: 'Payment', icon: DollarSign, description: 'Certification Fee', color: 'emerald' },
  { id: 3, name: 'Quantitative Assessment', shortName: 'Quantitative', icon: BarChart3, description: 'Data & Metrics', color: 'purple' },
  { id: 4, name: 'CIS Evaluation', shortName: 'Evaluation', icon: Clock, description: 'Expert Review (~2 weeks)', color: 'orange' },
  { id: 5, name: 'Results & Certification', shortName: 'Results', icon: Award, description: 'Your Certification', color: 'yellow' },
];

const EnhancedAssessmentPage = () => {
  const { assessmentId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [questions, setQuestions] = useState([]);
  const [responses, setResponses] = useState({});
  const [currentPillar, setCurrentPillar] = useState(1);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [companySize, setCompanySize] = useState(null);
  const [showWhyMatters, setShowWhyMatters] = useState(false);
  const [showQuickWin, setShowQuickWin] = useState(false);
  const [assessmentConfig, setAssessmentConfig] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [organizationName, setOrganizationName] = useState('');

  // Current phase is always 1 (Qualitative) for this page
  const currentPhase = 1;

  // Get organization ID from multiple sources
  const getOrganizationId = () => {
    if (location.state?.organizationId) return location.state.organizationId;
    if (localStorage.getItem('organization_id')) return localStorage.getItem('organization_id');
    if (sessionStorage.getItem('cness_organization_id')) return sessionStorage.getItem('cness_organization_id');
    try {
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      if (userData.organization_id) return userData.organization_id;
    } catch (e) {}
    return null;
  };

  const organizationId = getOrganizationId();

  useEffect(() => {
    fetchOrganizationData();
  }, []);

  useEffect(() => {
    if (companySize && organizationId) {
      generateAssessmentQuestions();
    }
  }, [companySize, organizationId]);

  const fetchOrganizationData = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/organizations/${organizationId}`);
      const data = await response.json();
      const size = data?.organization_size?.toUpperCase() || 'MEDIUM';
      setCompanySize(size);
      setOrganizationName(data?.name || 'Your Organization');
      
      const configResponse = await fetch(`${BACKEND_URL}/api/assessment/config/${size}`);
      const configData = await configResponse.json();
      setAssessmentConfig(configData);
    } catch (error) {
      console.error('Error fetching organization:', error);
      setCompanySize('MEDIUM');
    }
  };

  const generateAssessmentQuestions = async () => {
    try {
      setLoading(true);
      
      if (!organizationId) {
        fetchEnhancedQuestions();
        return;
      }
      
      const response = await fetch(`${BACKEND_URL}/api/assessment/generate-questions/${organizationId}`, {
        method: 'POST'
      });
      
      if (response.ok) {
        const data = await response.json();
        
        if (data.session_id) {
          setSessionId(data.session_id);
          const allQuestions = [
            ...(data.qualitative_questions || []),
            ...(data.quantitative_questions || [])
          ];
          setQuestions(allQuestions);
        }
      } else {
        fetchEnhancedQuestions();
      }
      fetchExistingResponses();
    } catch (error) {
      console.error('Error generating questions:', error);
      fetchEnhancedQuestions();
    } finally {
      setLoading(false);
    }
  };

  const fetchEnhancedQuestions = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/enhanced-assessment/questions`);
      const data = await response.json();
      setQuestions(data.questions || []);
      fetchExistingResponses();
    } catch (error) {
      console.error('Error fetching enhanced questions:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchExistingResponses = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/org-responses/${assessmentId}/${organizationId}?phase=qualitative`);
      const data = await response.json();
      
      const responsesMap = {};
      data.forEach(r => {
        responsesMap[r.question_id] = {
          selected_level: r.selected_level,
          response_text: r.response_text,
          best_practice: r.best_practice,
          improvement_idea: r.improvement_idea,
          additional_notes: r.additional_notes,
          status: r.status,
          response_id: r.response_id
        };
      });
      setResponses(responsesMap);
    } catch (error) {
      console.error('Error fetching responses:', error);
    }
  };

  const pillarQuestions = questions.filter(q => q.pillar_number === currentPillar);
  const currentQuestion = pillarQuestions[currentQuestionIndex];

  const handleSaveResponse = async (status = 'draft') => {
    if (!currentQuestion) return;

    setSaving(true);
    try {
      const currentResponse = responses[currentQuestion.question_id] || {};
      const responseData = {
        assessment_id: assessmentId,
        organization_id: organizationId,
        question_id: currentQuestion.question_id,
        phase: 'qualitative',
        pillar_number: currentQuestion.pillar_number,
        selected_level: currentResponse.selected_level || 0,
        response_text: currentResponse.response_text || '',
        best_practice: currentResponse.best_practice || '',
        improvement_idea: currentResponse.improvement_idea || '',
        additional_notes: currentResponse.additional_notes || '',
        status: status,
        submitted_by_user_id: organizationId
      };

      const response = await fetch(`${BACKEND_URL}/api/org-responses`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(responseData)
      });

      if (response.ok) {
        const result = await response.json();
        setResponses(prev => ({
          ...prev,
          [currentQuestion.question_id]: {
            ...prev[currentQuestion.question_id],
            response_id: result.response_id,
            status: status
          }
        }));
        
        if (status === 'submitted') {
          if (currentQuestionIndex < pillarQuestions.length - 1) {
            setCurrentQuestionIndex(currentQuestionIndex + 1);
          } else if (currentPillar < 8) {
            setCurrentPillar(currentPillar + 1);
            setCurrentQuestionIndex(0);
          }
        }
      }
    } catch (error) {
      console.error('Error saving response:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleResponseChange = (field, value) => {
    setResponses(prev => ({
      ...prev,
      [currentQuestion.question_id]: {
        ...prev[currentQuestion.question_id],
        [field]: value
      }
    }));
  };

  const handleLevelSelect = (level) => {
    handleResponseChange('selected_level', level);
    const rubric = currentQuestion.rubric?.[level.toString()];
    if (rubric) {
      handleResponseChange('response_text', rubric.description);
    }
  };

  const getResponseStatus = (questionId) => {
    return responses[questionId]?.status || 'not_started';
  };

  const getCompletionStats = () => {
    const submitted = Object.values(responses).filter(r => r.status === 'submitted').length;
    const qualitativeTotal = assessmentConfig?.config?.qualitative_questions || assessmentConfig?.question_distribution?.qualitative || questions.length;
    return {
      submitted,
      total: qualitativeTotal,
      percentage: Math.round((submitted / qualitativeTotal) * 100) || 0
    };
  };

  const shouldShowHelpers = () => {
    const showForSizes = ['MICRO', 'SMALL', 'MEDIUM'];
    return showForSizes.includes(companySize);
  };

  const stats = getCompletionStats();
  const fee = assessmentConfig?.fee || assessmentConfig?.config?.assessment_fee || 0;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-indigo-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-indigo-600 border-t-transparent mx-auto mb-4"></div>
          <p className="text-slate-600 font-medium text-lg">Preparing your assessment...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50">
      {/* Professional Header with Phase Indicator */}
      <div className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          {/* Top Bar */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/org-admin/assessments')}
                className="p-2 hover:bg-slate-100 rounded-lg transition-all text-slate-600"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-slate-900 tracking-tight">CNESS Assessment</h1>
                <p className="text-sm text-slate-500">{organizationName}</p>
              </div>
            </div>
            
            {/* Progress Indicator */}
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Phase 1 Progress</p>
                <p className="text-2xl font-bold text-indigo-600">{stats.submitted}/{stats.total}</p>
              </div>
              <div className="relative w-16 h-16">
                <svg className="transform -rotate-90 w-16 h-16">
                  <circle cx="32" cy="32" r="28" stroke="#E2E8F0" strokeWidth="4" fill="none"/>
                  <circle 
                    cx="32" cy="32" r="28" 
                    stroke="#4F46E5" 
                    strokeWidth="4" 
                    fill="none"
                    strokeDasharray={`${stats.percentage * 1.76} 176`}
                    strokeLinecap="round"
                    className="transition-all duration-500"
                  />
                </svg>
                <span className="absolute inset-0 flex items-center justify-center text-sm font-bold text-indigo-600">
                  {stats.percentage}%
                </span>
              </div>
            </div>
          </div>

          {/* Assessment Flow - Professional Phase Indicator */}
          <div className="bg-gradient-to-r from-slate-50 to-indigo-50 rounded-2xl p-4 border border-slate-200">
            <div className="flex items-center justify-between">
              {ASSESSMENT_PHASES.map((phase, idx) => {
                const isActive = phase.id === currentPhase;
                const isCompleted = phase.id < currentPhase;
                const Icon = phase.icon;
                
                return (
                  <React.Fragment key={phase.id}>
                    <div className={`flex-1 ${idx > 0 ? 'ml-2' : ''}`}>
                      <div className={`flex items-center gap-3 p-3 rounded-xl transition-all ${
                        isActive 
                          ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-200' 
                          : isCompleted 
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-white text-slate-400 border border-slate-200'
                      }`}>
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          isActive ? 'bg-white/20' : isCompleted ? 'bg-emerald-200' : 'bg-slate-100'
                        }`}>
                          {isCompleted ? (
                            <CheckCircle className="w-5 h-5 text-emerald-600" />
                          ) : (
                            <Icon className={`w-5 h-5 ${isActive ? 'text-white' : 'text-slate-400'}`} />
                          )}
                        </div>
                        <div className="hidden lg:block">
                          <p className={`text-xs font-medium uppercase tracking-wide ${isActive ? 'text-indigo-100' : ''}`}>
                            Phase {phase.id}
                          </p>
                          <p className={`text-sm font-bold ${isActive ? 'text-white' : ''}`}>
                            {phase.shortName}
                          </p>
                        </div>
                      </div>
                      {/* Phase details below */}
                      <p className="text-xs text-slate-500 text-center mt-2 hidden lg:block">
                        {phase.id === 1 && `${stats.total} Questions`}
                        {phase.id === 2 && `$${fee.toLocaleString()}`}
                        {phase.id === 3 && 'Data Metrics'}
                        {phase.id === 4 && '~2 Weeks'}
                        {phase.id === 5 && 'Your Score'}
                      </p>
                    </div>
                    {idx < ASSESSMENT_PHASES.length - 1 && (
                      <ArrowRight className={`w-5 h-5 mx-2 flex-shrink-0 ${
                        phase.id < currentPhase ? 'text-emerald-400' : 'text-slate-300'
                      }`} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Current Phase Banner */}
      <div className="bg-gradient-to-r from-indigo-600 via-indigo-700 to-purple-700 text-white">
        <div className="max-w-7xl mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center backdrop-blur-sm">
                <FileText className="w-6 h-6" />
              </div>
              <div>
                <p className="text-indigo-200 text-sm font-medium uppercase tracking-wider">Phase 1 of 5</p>
                <h2 className="text-xl font-bold">Qualitative Self-Assessment</h2>
              </div>
            </div>
            <div className="hidden md:flex items-center gap-6">
              <div className="text-center">
                <p className="text-indigo-200 text-xs">Company Size</p>
                <p className="text-lg font-bold">{companySize}</p>
              </div>
              <div className="w-px h-10 bg-white/20"></div>
              <div className="text-center">
                <p className="text-indigo-200 text-xs">Questions</p>
                <p className="text-lg font-bold">{stats.total}</p>
              </div>
              <div className="w-px h-10 bg-white/20"></div>
              <div className="text-center">
                <p className="text-indigo-200 text-xs">Completed</p>
                <p className="text-lg font-bold">{stats.submitted}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-12 gap-8">
          {/* Sidebar - Pillar Navigation */}
          <div className="col-span-12 lg:col-span-3">
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5 sticky top-6">
              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4">6 Pillars</h3>
              <div className="space-y-2">
                {ACTIVE_PILLAR_NUMBERS.map(pillar => {
                  const pillarQs = questions.filter(q => q.pillar_number === pillar);
                  const pillarName = pillarQs[0]?.pillar_name || `Pillar ${pillar}`;
                  const completed = pillarQs.filter(q => getResponseStatus(q.question_id) === 'submitted').length;
                  const isComplete = completed === pillarQs.length && pillarQs.length > 0;
                  const isActive = currentPillar === pillar;
                  
                  return (
                    <button
                      key={pillar}
                      onClick={() => {
                        setCurrentPillar(pillar);
                        setCurrentQuestionIndex(0);
                      }}
                      className={`w-full text-left px-4 py-3 rounded-xl transition-all ${
                        isActive
                          ? 'bg-indigo-600 text-white shadow-md'
                          : isComplete
                          ? 'bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100'
                          : 'bg-slate-50 text-slate-700 hover:bg-slate-100 border border-transparent'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 min-w-0">
                          {isComplete && !isActive && <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />}
                          <span className="font-medium text-sm truncate">{pillarName}</span>
                        </div>
                        <span className={`text-xs font-bold px-2 py-1 rounded-full flex-shrink-0 ${
                          isActive ? 'bg-white/20 text-white' : isComplete ? 'bg-emerald-200 text-emerald-700' : 'bg-slate-200 text-slate-600'
                        }`}>
                          {completed}/{pillarQs.length}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Main Question Area */}
          <div className="col-span-12 lg:col-span-9">
            {currentQuestion ? (
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                {/* Question Header */}
                <div className="bg-gradient-to-r from-slate-800 to-slate-900 text-white px-8 py-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <span className="bg-white/10 px-3 py-1.5 rounded-lg text-sm font-mono font-medium">
                        {currentQuestion.question_id}
                      </span>
                      <span className="text-slate-400 text-sm">
                        {currentQuestion.sub_pillar}
                      </span>
                    </div>
                    <span className={`px-4 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wide ${
                      getResponseStatus(currentQuestion.question_id) === 'submitted'
                        ? 'bg-emerald-500 text-white'
                        : 'bg-white/10 text-slate-300'
                    }`}>
                      {getResponseStatus(currentQuestion.question_id) === 'submitted' ? '✓ Completed' : 'In Progress'}
                    </span>
                  </div>
                  <h2 className="text-lg font-bold text-slate-200 mb-1">{currentQuestion.pillar_name}</h2>
                  <p className="text-slate-400 text-sm">Question {currentQuestionIndex + 1} of {pillarQuestions.length}</p>
                </div>

                {/* Question Content */}
                <div className="p-8">
                  {/* Question Text - Professional Typography */}
                  <div className="mb-8">
                    <h3 className="text-2xl font-bold text-slate-900 leading-relaxed tracking-tight">
                      {currentQuestion.question_text}
                    </h3>
                    
                    {/* Why This Matters */}
                    {shouldShowHelpers() && currentQuestion.why_this_matters?.enabled && (
                      <div className="mt-6">
                        <button
                          onClick={() => setShowWhyMatters(!showWhyMatters)}
                          className="flex items-center gap-2 text-indigo-600 hover:text-indigo-800 font-medium text-sm transition-colors"
                        >
                          <HelpCircle className="w-4 h-4" />
                          <span>Why This Matters</span>
                          <ChevronRight className={`w-4 h-4 transition-transform ${showWhyMatters ? 'rotate-90' : ''}`} />
                        </button>
                        {showWhyMatters && (
                          <div className="mt-3 bg-indigo-50 border-l-4 border-indigo-500 p-4 rounded-r-xl">
                            <p className="text-sm text-indigo-800 leading-relaxed">{currentQuestion.why_this_matters.text}</p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Quick Win */}
                    {shouldShowHelpers() && currentQuestion.quick_win?.enabled && (
                      <div className="mt-4">
                        <button
                          onClick={() => setShowQuickWin(!showQuickWin)}
                          className="flex items-center gap-2 text-emerald-600 hover:text-emerald-800 font-medium text-sm transition-colors"
                        >
                          <Lightbulb className="w-4 h-4" />
                          <span>Quick Win Suggestion</span>
                          <ChevronRight className={`w-4 h-4 transition-transform ${showQuickWin ? 'rotate-90' : ''}`} />
                        </button>
                        {showQuickWin && (
                          <div className="mt-3 bg-emerald-50 border-l-4 border-emerald-500 p-4 rounded-r-xl">
                            <p className="text-sm text-emerald-800 leading-relaxed">{currentQuestion.quick_win.text}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* C-N-E-S-S Journey Selection */}
                  <div className="mb-8">
                    <label className="block text-sm font-bold text-slate-700 mb-4 uppercase tracking-wide">
                      Select Your Current Level
                    </label>
                    <div className="grid grid-cols-5 gap-4">
                      {SCORING_JOURNEY.map((level) => {
                        const rubric = currentQuestion.rubric?.[level.level.toString()];
                        const isSelected = responses[currentQuestion.question_id]?.selected_level === level.level;
                        
                        return (
                          <button
                            key={level.level}
                            onClick={() => handleLevelSelect(level.level)}
                            className={`relative p-5 rounded-2xl border-2 transition-all hover:shadow-lg ${
                              isSelected
                                ? `${level.borderColor} ${level.bgColor} shadow-lg ring-2 ring-offset-2 ring-indigo-500`
                                : 'border-slate-200 hover:border-slate-300 bg-white'
                            }`}
                          >
                            {isSelected && (
                              <div className="absolute -top-2 -right-2 w-6 h-6 bg-indigo-600 rounded-full flex items-center justify-center">
                                <CheckCircle className="w-4 h-4 text-white" />
                              </div>
                            )}
                            <div className="text-center">
                              <div className={`w-14 h-14 mx-auto rounded-xl ${level.bgColor} flex items-center justify-center text-3xl mb-3`}>
                                {level.icon}
                              </div>
                              <div className={`text-xl font-black ${level.textColor}`}>{level.letter}</div>
                              <div className="text-xs font-bold text-slate-600 mt-1">{level.label}</div>
                              {rubric && (
                                <div className="text-xs text-slate-500 mt-2 line-clamp-2 leading-relaxed">
                                  {rubric.short_label || rubric.gamified_label}
                                </div>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                    
                    {/* Selected Level Description */}
                    {responses[currentQuestion.question_id]?.selected_level && (
                      <div className="mt-6 p-5 bg-slate-50 rounded-xl border border-slate-200">
                        <p className="text-sm text-slate-700 leading-relaxed">
                          <span className="font-bold text-slate-900">Your Selection: </span>
                          {currentQuestion.rubric?.[responses[currentQuestion.question_id]?.selected_level]?.description}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Additional Notes */}
                  {currentQuestion.additional_notes_enabled && (
                    <div className="mb-6">
                      <label className="block text-sm font-bold text-slate-700 mb-2 flex items-center gap-2">
                        <FileText className="w-4 h-4 text-slate-500" />
                        {currentQuestion.additional_notes_prompt || 'Additional Comments'}
                        <span className="text-xs font-normal text-slate-400">(Optional)</span>
                      </label>
                      <textarea
                        value={responses[currentQuestion.question_id]?.additional_notes || ''}
                        onChange={(e) => handleResponseChange('additional_notes', e.target.value)}
                        placeholder="Add any additional context or comments..."
                        rows={3}
                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-slate-800 placeholder:text-slate-400"
                      />
                    </div>
                  )}

                  {/* Best Practices */}
                  {currentQuestion.best_practices?.enabled && (
                    <div className="mb-6 bg-amber-50 p-5 rounded-xl border border-amber-200">
                      <label className="block text-sm font-bold text-amber-800 mb-2 flex items-center gap-2">
                        <Star className="w-4 h-4" />
                        {currentQuestion.best_practices.prompt || 'Share a Best Practice'}
                        <span className="text-xs font-normal text-amber-600">(Optional)</span>
                      </label>
                      <textarea
                        value={responses[currentQuestion.question_id]?.best_practice || ''}
                        onChange={(e) => handleResponseChange('best_practice', e.target.value)}
                        placeholder="Share something you're proud of in this area..."
                        rows={3}
                        className="w-full px-4 py-3 border border-amber-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white text-slate-800"
                      />
                    </div>
                  )}

                  {/* Continuous Improvement */}
                  {currentQuestion.continuous_improvement?.enabled && (
                    <div className="mb-6 bg-purple-50 p-5 rounded-xl border border-purple-200">
                      <label className="block text-sm font-bold text-purple-800 mb-2 flex items-center gap-2">
                        <Sparkles className="w-4 h-4" />
                        {currentQuestion.continuous_improvement.prompt || "What's one thing you'd like to improve?"}
                        <span className="text-xs font-normal text-purple-600">(Optional)</span>
                      </label>
                      <textarea
                        value={responses[currentQuestion.question_id]?.improvement_idea || ''}
                        onChange={(e) => handleResponseChange('improvement_idea', e.target.value)}
                        placeholder="Share any improvement ideas..."
                        rows={3}
                        className="w-full px-4 py-3 border border-purple-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white text-slate-800"
                      />
                    </div>
                  )}

                  {/* Evidence Upload */}
                  {currentQuestion.evidence_type !== 'NONE' && (
                    <div className="mb-6">
                      <label className="block text-sm font-bold text-slate-700 mb-2 flex items-center gap-2">
                        <Upload className="w-4 h-4 text-slate-500" />
                        {currentQuestion.evidence_prompt || 'Supporting Evidence'}
                        <span className={`text-xs ml-2 px-2 py-0.5 rounded-full ${
                          currentQuestion.evidence_type === 'REQUIRED' 
                            ? 'bg-red-100 text-red-700' 
                            : 'bg-slate-100 text-slate-600'
                        }`}>
                          {currentQuestion.evidence_type === 'REQUIRED' ? 'Required' : 'Optional'}
                        </span>
                      </label>
                      <div className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center hover:border-indigo-400 transition-colors cursor-pointer bg-slate-50">
                        <FileText className="w-10 h-10 text-slate-400 mx-auto mb-3" />
                        <p className="text-sm font-medium text-slate-600">Drag and drop files or click to browse</p>
                        <p className="text-xs text-slate-400 mt-1">
                          Accepts: {currentQuestion.attachment_types?.join(', ') || 'document, link'}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex items-center justify-between pt-6 border-t border-slate-200">
                    <button
                      onClick={() => {
                        if (currentQuestionIndex > 0) {
                          setCurrentQuestionIndex(currentQuestionIndex - 1);
                        } else if (currentPillar > 1) {
                          setCurrentPillar(currentPillar - 1);
                          const prevPillarQs = questions.filter(q => q.pillar_number === currentPillar - 1);
                          setCurrentQuestionIndex(prevPillarQs.length - 1);
                        }
                      }}
                      disabled={currentPillar === 1 && currentQuestionIndex === 0}
                      className="flex items-center gap-2 px-5 py-3 bg-slate-100 text-slate-700 rounded-xl font-semibold hover:bg-slate-200 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="w-5 h-5" />
                      Previous
                    </button>

                    <div className="flex gap-3">
                      <button
                        onClick={() => handleSaveResponse('draft')}
                        disabled={saving}
                        className="flex items-center gap-2 px-5 py-3 bg-slate-100 text-slate-700 rounded-xl font-semibold hover:bg-slate-200 transition-all disabled:opacity-50"
                      >
                        <Save className="w-5 h-5" />
                        {saving ? 'Saving...' : 'Save Draft'}
                      </button>

                      <button
                        onClick={() => handleSaveResponse('submitted')}
                        disabled={saving || !responses[currentQuestion.question_id]?.selected_level}
                        className="flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-xl font-semibold hover:bg-indigo-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
                      >
                        <Send className="w-5 h-5" />
                        {saving ? 'Submitting...' : 'Submit & Continue'}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Question Navigation Footer */}
                <div className="bg-slate-50 px-8 py-4 border-t border-slate-200">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-slate-600">
                      <span className="font-medium">Pillar {currentPillar}</span> • Question {currentQuestionIndex + 1} of {pillarQuestions.length}
                    </p>
                    <div className="flex gap-1.5">
                      {pillarQuestions.map((q, idx) => (
                        <button
                          key={q.question_id}
                          onClick={() => setCurrentQuestionIndex(idx)}
                          className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold transition-all ${
                            idx === currentQuestionIndex
                              ? 'bg-indigo-600 text-white shadow-md'
                              : getResponseStatus(q.question_id) === 'submitted'
                              ? 'bg-emerald-500 text-white'
                              : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
                          }`}
                        >
                          {idx + 1}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-12 text-center">
                <AlertCircle className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500 text-lg">No questions available for this pillar</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnhancedAssessmentPage;
